import {
  CircleCheck,
  Info,
  Circle,
  OctagonX,
  TriangleAlert,
} from "lucide-react"
import { useTheme } from "next-themes"
import { Toaster as Sonner } from "sonner"

type ToasterProps = React.ComponentProps<typeof Sonner>

const Toaster = ({ ...props }: ToasterProps) => {
  const { theme = "system" } = useTheme()

  return (
    <Sonner
      theme={theme as ToasterProps["theme"]}
      className="toaster group"
      // Centred just under the top bar: TopNav is h-16 (64px) plus its 1px bottom
      // border, so 68px leaves a 3px gap. The reader's chapter bar sits below it.
      position="top-center"
      // No X: swipe to dismiss (native in sonner) or wait it out.
      duration={3500}
      visibleToasts={1}
      offset={{ top: 68 }}
      mobileOffset={{ top: "calc(68px + env(safe-area-inset-top))" }}
      icons={{
        success: <CircleCheck className="h-3.5 w-3.5" />,
        info: <Info className="h-3.5 w-3.5" />,
        warning: <TriangleAlert className="h-3.5 w-3.5" />,
        error: <OctagonX className="h-3.5 w-3.5" />,
        loading: <Circle className="h-3.5 w-3.5 fill-current" />,
      }}
      toastOptions={{
        // Sized to match DraftRebuildChip (the "Updating dictionary…" chip) —
        // text-xs, the same padding/gap scale — instead of sonner's default
        // 16px/13px chrome, so a toast reads as a small transient note next
        // to that chip rather than a bigger, competing shape. Shorter on phones
        // so it ends above the first row of page controls under the header.
        classNames: {
          toast:
            "group toast group-[.toaster]:bg-background group-[.toaster]:text-foreground group-[.toaster]:border-border group-[.toaster]:shadow-md !px-3 !py-2 sm:!py-3 !gap-2 !text-xs !rounded-lg !max-w-[190px] sm:!max-w-[240px]",
          title: "!text-xs !leading-tight",
          description: "group-[.toast]:text-muted-foreground !text-[11px] !leading-snug",
          actionButton:
            "group-[.toast]:bg-primary group-[.toast]:text-primary-foreground !h-6 !px-2 !text-[11px]",
          cancelButton:
            "group-[.toast]:bg-muted group-[.toast]:text-muted-foreground !h-6 !px-2 !text-[11px]",
          icon: "!h-3.5 !w-3.5",
          // Distinct per-type looks so a failure never reads the same as a
          // success. These classes sit on the toast element itself, so they
          // must be plain (a group-[.toast]: variant only matches children).
          // The tint is mixed into the page background so it stays opaque.
          error:
            "!border-destructive/70 ![background-color:color-mix(in_srgb,oklch(var(--destructive))_22%,oklch(var(--background)))] [&_[data-icon]]:!text-destructive",
          warning:
            "!border-warning/70 ![background-color:color-mix(in_srgb,oklch(var(--warning))_18%,oklch(var(--background)))] [&_[data-icon]]:!text-warning",
          success:
            "!border-success/60 ![background-color:color-mix(in_srgb,oklch(var(--success))_14%,oklch(var(--background)))] [&_[data-icon]]:!text-success",
        },
      }}
      {...props}
    />
  )
}

export { Toaster }
